import { Explicit } from "../../components/ui/Explicit";
import { DrawerLink } from "../../app/DrawerLink";

import { FavoriteButton } from "../../components/ui/FavoriteButton";
import { BufferRing } from "../../components/ui/Loader";
import { usePlayer } from "../../stores/player";
import { useRoomTuning } from "../../stores/room";
import { BroadcastBar } from "./BroadcastBar";
import { Waveform } from "./Waveform";

/**
 * The dock's contents (HANDOFF §9.2).
 *
 * Reads the store; owns no audio. `PlayerHost` holds the element, and keeping
 * them separate means this can re-render as often as it likes with no risk of
 * tearing down playback.
 */
export function PlayerBar() {
  /*
   * A broadcast is a different instrument, so it gets a different bar.
   *
   * Not this one with disabled buttons: every transport control here either
   * does nothing during a broadcast or quietly drops you out of it, and the
   * honest answer is to show the controls that are true instead.
   */
  const tuned = useRoomTuning((s) => s.tuned);

  const track = usePlayer((s) => s.track);
  const playing = usePlayer((s) => s.playing);
  const loading = usePlayer((s) => s.loading);
  const error = usePlayer((s) => s.error);
  const position = usePlayer((s) => s.position);
  const duration = usePlayer((s) => s.duration);
  const volume = usePlayer((s) => s.volume);
  const muted = usePlayer((s) => s.muted);

  const toggle = usePlayer((s) => s.toggle);
  const next = usePlayer((s) => s.next);
  const previous = usePlayer((s) => s.previous);
  const seek = usePlayer((s) => s.seek);
  const setVolume = usePlayer((s) => s.setVolume);
  const toggleMute = usePlayer((s) => s.toggleMute);

  if (tuned) {
    /*
     * Nothing for a studio broadcast.
     *
     * This bar is a transport for audio the dock is playing, and in studio mode
     * it is playing none: the sound arrives inside a video element on the
     * host's profile. Pause and volume here would control silence, and the
     * listening state it reports does not survive leaving that page anyway -
     * so the profile owns the controls and this stays out of the way.
     */
    if (tuned.studio) return null;

    return (
      <BroadcastBar
        username={tuned.username}
        host={tuned.host}
        name={tuned.name}
      />
    );
  }

  if (!track) {
    // Full width, matching the top bar. Two pieces of chrome disagreeing about
    // where the page edge is looks like a mistake, because it is.
    return (
      <div className="flex h-full items-center gap-4 px-6">
        <div
          className="h-11 w-11 shrink-0 rounded-[var(--radius-control)] bg-raised"
          aria-hidden
        />
        <div className="min-w-0">
          <div className="text-sm text-faint">Nothing playing</div>
          <div className="meta text-faint">pick a track to start</div>
        </div>
      </div>
    );
  }

  // Falls back to the stored duration until the element reports its own, so
  // the bar does not read 0:00 for the first second of every track.
  const total = duration || (track.duration_ms ?? 0) / 1000;
  const progress = total > 0 ? Math.min(1, position / total) : 0;

  return (
    /*
     * A labelled region, not a bare div.
     *
     * The dock outlives every navigation, so to somebody using a screen reader
     * it is a set of controls with no announced identity that simply persists -
     * and its Play button is indistinguishable from the Play buttons in a track
     * list. Naming it is what separates "pause the dock" from "pause that row".
     */
    <div
      role="region"
      aria-label="Now playing"
      data-player-dock
      className="flex h-full items-center gap-4 px-6"
    >
      <DrawerLink
        to={`/tracks/${track.id}`}
        className="flex min-w-0 shrink-0 items-center gap-3"
      >
        {(track.artwork_small ?? track.artwork) ? (
          <img
            // The 200px variant into a 44px slot. The 800px one is eight times
            // the bytes for something that looks no better at this size.
            src={track.artwork_small ?? track.artwork ?? undefined}
            alt=""
            className="h-11 w-11 rounded-[var(--radius-control)] object-cover"
          />
        ) : (
          <div
            aria-hidden
            className="h-11 w-11 shrink-0 rounded-[var(--radius-control)]"
            style={{
              backgroundImage:
                "repeating-linear-gradient(45deg, #151530 0 6px, #101024 6px 12px)",
            }}
          />
        )}

        <div className="min-w-0 max-w-[220px]">
          <div className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
            {track.explicit && <Explicit />}
            <span className="truncate">{track.title}</span>
          </div>
          <div className="meta truncate text-faint">
            {track.owner?.display_name ?? ""}
          </div>
        </div>
      </DrawerLink>

      <div className="flex shrink-0 items-center gap-1">
        <Control label="Previous" onClick={previous}>
          <svg
            viewBox="0 0 16 16"
            className="h-4 w-4"
            fill="currentColor"
            aria-hidden
          >
            <path d="M4 3h2v10H4zM13 3v10L6 8z" />
          </svg>
        </Control>

        <button
          type="button"
          onClick={toggle}
          aria-label={playing ? "Pause" : "Play"}
          className="flex h-10 w-10 items-center justify-center rounded-full bg-accent text-accent-ink transition-colors hover:bg-accent-hover"
        >
          {/*
            The buffer ring is reserved for this - a track waiting on audio -
            which is why nothing else on the site uses it. Somebody who sees it
            over the dock knows their track is loading and not that some
            unrelated page is.
          */}
          {loading ? (
            <BufferRing size={22} />
          ) : playing ? (
            <svg
              viewBox="0 0 16 16"
              className="h-4 w-4"
              fill="currentColor"
              aria-hidden
            >
              <path d="M4 3h3v10H4zM9 3h3v10H9z" />
            </svg>
          ) : (
            <svg
              viewBox="0 0 16 16"
              className="h-4 w-4"
              fill="currentColor"
              aria-hidden
            >
              <path d="M5 3l8 5-8 5z" />
            </svg>
          )}
        </button>

        <Control label="Next" onClick={next}>
          <svg
            viewBox="0 0 16 16"
            className="h-4 w-4"
            fill="currentColor"
            aria-hidden
          >
            <path d="M12 3h-2v10h2zM3 3v10l7-5z" />
          </svg>
        </Control>

        {/*
          Favouriting from the dock, which is the case the whole store exists
          for: whatever page you are on, the thing playing is favouritable, and
          the row you originally clicked may have unmounted several navigations
          ago. `track.favorited` is what came down with it; the store's overlay
          wins if anything has changed it since.
        */}
        <FavoriteButton
          trackId={track.id}
          favorited={track.favorited}
          likeCount={track.like_count ?? 0}
          size="sm"
          className="ml-1"
        />
      </div>

      <div className="flex min-w-0 flex-1 items-center gap-3">
        <span className="meta shrink-0 tabular-nums text-faint">
          {clock(position)}
        </span>

        <Waveform
          url={track.waveform_url}
          progress={progress}
          height={36}
          onSeek={(fraction) => seek(fraction * total)}
          className="min-w-0 flex-1"
        />

        <span className="meta shrink-0 tabular-nums text-faint">
          {clock(total)}
        </span>
      </div>

      <div className="hidden shrink-0 items-center gap-2 lg:flex">
        <button
          type="button"
          onClick={toggleMute}
          aria-label={muted ? "Unmute" : "Mute"}
          className="text-soft hover:text-ink"
        >
          <svg
            viewBox="0 0 16 16"
            className="h-4 w-4"
            fill="currentColor"
            aria-hidden
          >
            {muted || volume === 0 ? (
              <path
                d="M7 3L4 6H2v4h2l3 3zM10.5 6l3 4M13.5 6l-3 4"
                stroke="currentColor"
                fill="none"
                strokeWidth="1.4"
              />
            ) : (
              <path
                d="M7 3L4 6H2v4h2l3 3zM10 5.5a3.5 3.5 0 010 5M12 3.5a6.5 6.5 0 010 9"
                stroke="currentColor"
                fill="none"
                strokeWidth="1.4"
              />
            )}
          </svg>
        </button>

        <input
          type="range"
          min={0}
          max={1}
          step={0.01}
          value={muted ? 0 : volume}
          onChange={(event) => setVolume(Number(event.target.value))}
          aria-label="Volume"
          className="h-1 w-20 accent-[var(--color-signal)]"
        />
      </div>

      {error && <span className="meta shrink-0 text-hot-text">{error}</span>}
    </div>
  );
}

function Control({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      className="flex h-8 w-8 items-center justify-center rounded-full text-soft transition-colors hover:bg-raised hover:text-ink"
    >
      {children}
    </button>
  );
}

/** `3:41`. Hours only when there are any, so a four-minute track is not `0:03:41`. */
function clock(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";

  const total = Math.floor(seconds);
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const secs = total % 60;

  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
    : `${minutes}:${String(secs).padStart(2, "0")}`;
}
